import { html, css, PropertyValues } from "lit";
import { customElement, property, query, state } from "lit/decorators.js";
import { Overlay } from "@omegagrid/core";
import { msg } from "@omegagrid/localize";
import { MarkdownLinkEvent, MarkdownView } from "@omegagrid/markdown";
import rp, { AppComponent, helpers } from "@reporting/rp-app-host";
import { Page } from "@reporting/rp-types/wiki";
import { buildWikiImageUrl, buildWikiPageUrl, findPageByName, isExternalLink, pageComponentId, splitLinkTarget, wikiPagePath } from "../helpers";

@customElement('rp-wiki-page-container')
export class PageContainer extends AppComponent {

	static styles = css`
		:host {
			display: flex;
			flex-direction: column;
			width: 100%;
			height: 100%;
			position: relative;
		}

		og-container {
			height: 100%;
			overflow: hidden;
		}

		og-markdown-view {
			width: 100%;
			height: 100%;
		}
	`;

	@property({type: Number})
	a: number = 0;

	@property({type: Number})
	pageId: number;

	@property({type: String})
	lang: string;

	@query('og-overlay')
	loader: Overlay;

	@query('og-markdown-view')
	markdown: MarkdownView;

	@state()
	private _content: string | undefined;

	/** the heading to scroll to once the view exists (a link to a section of another page, an address with a fragment) */
	private _pendingAnchor: string;

	async loadContent() {
		if (!this.pageId) {
			this._content = '';
			return;
		}

		this.loader.show(this);
		// a guest sees one page at a time, so the address follows it; a user has the page in a tab among others
		if (rp.router.anonymousAccess) this.updateUrl();
		const raw = await rp.ms('wiki').get<string>(`pages/${this.pageId}/content`, { lang: this.lang, accounting: this.a });
		this._content = raw.length ? helpers.fixMarkdown(raw) : msg('No content available');
		this.loader.hide();
	}

	/**
	 * The address of the one page a guest sees (wikiPagePath, /wiki/<name>); the address of a user keeps
	 * naming the page it opened with (buildWikiPageUrl), the pages opened later are tabs.
	 */
	private async updateUrl() {
		const pageId = this.pageId;
		const page = await rp.ms('wiki').get<Page>(`pages/${pageId}`, { lang: this.lang, accounting: this.a, fields: ['name'] });
		if (page?.name && pageId === this.pageId) rp.router.setPath(wikiPagePath(this.a, page.name));
	}

	async updated(changedProperties: PropertyValues) {
		if (this._pendingAnchor && this.markdown) {
			this.markdown.anchor = this._pendingAnchor;
			this._pendingAnchor = null;
		}
		if (!changedProperties.has('pageId') && !changedProperties.has('lang')) return;
		await this.loadContent();
	}

	refresh = () => this.loadContent();

	/** Scrolls to a heading of the page (its id), now or once the content has rendered (the view keeps it in place while the content settles). */
	scrollToAnchor(anchor: string) {
		if (this.markdown) this.markdown.anchor = anchor;
		else this._pendingAnchor = anchor;
	}

	_onLink = async (e: MarkdownLinkEvent) => {
		e.preventDefault();
		// the destination as written in the markdown; the rendered href is the address of the page (_modifyLinkUrl)
		const href = e.href ?? e.a.getAttribute('href');
		if (!href) return;
		if (href.startsWith('#')) {
			this.markdown.scrollToSelector(href);
			return;
		}
		if (isExternalLink(href)) {
			window.open(href, '_blank');
			return;
		}
		const { name, anchor } = splitLinkTarget(href);
		const page = await findPageByName(this.a, name, this.lang);
		if (!page) return;
		const container = await rp.host.open<PageContainer>(pageComponentId(this.a, page.id_wiki_page), { title: page.name });
		if (anchor) container?.scrollToAnchor(anchor);
	}

	_modifyImageUrl = (url: string) => {
		return buildWikiImageUrl(this.a, url);
	}

	/**
	 * A link to another page is written as the page name; the browser would resolve that relative to the
	 * current address, so the rendered href is the address of the page instead (what a copied link gets).
	 */
	_modifyLinkUrl = (href: string) => {
		if (!href || href.startsWith('#') || isExternalLink(href)) return href;
		const { name, anchor } = splitLinkTarget(href);
		return buildWikiPageUrl(this.a, name, anchor);
	}

	render = () => html`
		<og-overlay defaultLoader></og-overlay>
		${this._content !== undefined ? html`
			<og-markdown-view
				.content="${this._content}"
				maxContentWidth="2000"
				.modifyImageUrl="${this._modifyImageUrl}"
				.modifyLinkUrl="${this._modifyLinkUrl}"
				@markdown.link="${this._onLink}">
			</og-markdown-view>
		` : ''}
	`;

}
